/**
 * The routes.
 *
 * Screens that are more than "a table of one kind" are named here. Everything
 * else falls through to the generic entity routes, which are built from the
 * registry - so a kind added to services/schema.py gets a working table and
 * form without a route being written for it.
 */

import { Navigate, Route, Routes } from 'react-router-dom'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import BuildLabel from './components/common/BuildLabel'
import ErrorBoundary from './components/common/ErrorBoundary'
import Layout from './components/common/Layout'
import ProjectGate from './components/common/ProjectGate'
import Dashboard from './screens/Dashboard'
import EntityDetail from './screens/EntityDetail'
import EntityList from './screens/EntityList'
import Findings from './screens/Findings'
import Flowdown from './screens/Flowdown'
import Glossary from './screens/Glossary'
import ModelTree from './screens/ModelTree'
import SystemSummary from './screens/SystemSummary'
import NotFound from './screens/NotFound'
import Parts from './screens/Parts'
import States from './screens/States'
import Interfaces from './screens/Interfaces'
import Requirements from './screens/Requirements'
import SignIn from './screens/SignIn'
import Traceability from './screens/Traceability'
import VerificationFlowdown from './screens/VerificationFlowdown'
import VerificationTraceability from './screens/VerificationTraceability'
import VersionControl from './screens/VersionControl'
import Views from './screens/Views'
import { AppDataProvider } from './state/AppData'
import { ProjectProvider } from './state/Project'
import { UnsavedProvider } from './state/Unsaved'
import { ToastProvider } from './state/Toasts'

function Loading() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
      <CircularProgress />
    </Box>
  )
}

export default function App() {
  return (
    <>
      <AppDataProvider
        fallback={<Loading />}
        onError={(error, retry) => <SignIn error={error} onRetry={retry} />}
      >
        <ToastProvider>
          <ProjectProvider>
            <UnsavedProvider>
              <ProjectGate>
                <AppRoutes />
              </ProjectGate>
            </UnsavedProvider>
          </ProjectProvider>
        </ToastProvider>
      </AppDataProvider>
      {/* Beside the providers and not inside them: it is fixed to the corner,
          needs nobody signed in, and so is drawn on the sign-in screen and the
          program picker as well as on every screen of the shell. */}
      <BuildLabel />
    </>
  )
}

function AppRoutes() {
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route
          index
          element={
            <ErrorBoundary title="Dashboard failed">
              <Dashboard />
            </ErrorBoundary>
          }
        />
        {/* Screens that are more than a table of one kind.

            The two verification paths are NESTED under /verification, which the
            generic ':plural/:id' route below would otherwise swallow - React
            Router ranks a static segment above a dynamic one, so 'flowdown' is
            read as a screen rather than as an element called flowdown. The
            sidebar resolves the same overlap by longest match.

            'files' is gone: the .sysml export lives on the model tree now. */}
        {[
          ['findings', <Findings />, 'Findings failed'],
          ['glossary', <Glossary />, 'Glossary failed'],
          ['flowdown', <Flowdown />, 'Flowdown failed'],
          ['rtm', <Traceability />, 'Traceability failed'],
          ['requirements', <Requirements />, 'Requirements failed'],
          // Definitions and tree. The generic ':plural' route would answer
          // /parts with a flat table of definitions AND usages, which is what
          // this screen exists to replace; /parts/{id} still falls through to
          // the generated form below.
          ['parts', <Parts />, 'Parts failed'],
          // Machines rather than a flat table of every state as a sibling row,
          // which is what the generic ':plural' route answers with and what
          // this screen exists to replace. /states/{id} still falls through to
          // the generated form below.
          ['states', <States />, 'States failed'],
          // Interfaces, port defs, messages and the types interfaces are built
          // from, each created by its own questions (issue #52). /interfaces/{id}
          // still falls through to the generated editor below.
          ['interfaces', <Interfaces key="interfaces" />, 'Interfaces failed'],
          ['system-summary', <SystemSummary />, 'System summary failed'],
          ['model-tree', <ModelTree />, 'Model tree failed'],
          ['views', <Views />, 'Views failed'],
          ['verification/flowdown', <VerificationFlowdown />, 'Verification flowdown failed'],
          ['verification/rtm', <VerificationTraceability />, 'RVTM failed'],
          ['version-control', <VersionControl />, 'Version control failed'],
        ].map(([path, element, title]) => (
          <Route
            key={path as string}
            path={path as string}
            element={
              <ErrorBoundary title={title as string}>
                {element as React.ReactElement}
              </ErrorBoundary>
            }
          />
        ))}

        {/* The paths this screen has had. Kept as REDIRECTS rather than
            dropped: the generic ':plural' route below would otherwise answer
            /budgets with an empty table of a kind nothing declares, which reads
            as a program with no budgets rather than as a moved page. */}
        <Route path="budgets" element={<Navigate to="/system-summary" replace />} />
        <Route path="system-attributes" element={<Navigate to="/system-summary" replace />} />

        {/* Generic, and last: every named screen above wins on its own path,
            and anything else is treated as an entity kind. A kind added to the
            registry is reachable through these two without a route being
            written for it. */}
        <Route
          path=":plural"
          element={
            <ErrorBoundary title="This list failed">
              <EntityList />
            </ErrorBoundary>
          }
        />
        <Route
          path=":plural/:id"
          element={
            <ErrorBoundary title="This entity failed">
              <EntityDetail />
            </ErrorBoundary>
          }
        />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  )
}
